import { useState } from 'react';
import { Button, Card, Modal } from '@playerbase/ui';
import {
  NPS_OPTIONS,
  RATING_VALUES,
  SEVERITY_LEVELS,
  resolveBinaryLabels,
  resolveRatingDisplay,
  resolveSelectConfig,
} from '@playerbase/types';

import type {
  BuilderEvent,
  BuilderQuestion,
} from '../../../../games/use-playtest-builder-state.js';

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  events: BuilderEvent[];
}

// Preview of the playtest DM + modal flow. Two surfaces per event:
//
//   1. The DM bubble: the tester sees the event's headline plus a button
//      labelled by `buttonLabel` (defaults to "Share feedback").
//   2. The modal opened by the button: shows the event's questions
//      rendered the same way the feedback-form modal preview does.
//
// We deliberately copy the Discord-flavoured chrome that the form
// preview uses; the surface's job is to look like Discord, not the rest
// of the portal.
export function PreviewModal({ open, onClose, events }: PreviewModalProps): JSX.Element {
  const [eventIndex, setEventIndex] = useState(0);
  const safeIndex = Math.min(eventIndex, Math.max(0, events.length - 1));
  const current = events[safeIndex];

  return (
    <Modal open={open} onClose={onClose} labelledById="playtest-preview-title" size="lg">
      <Card>
        <Card.Header>
          <Card.Title id="playtest-preview-title">Preview</Card.Title>
          <Card.Description>
            How testers will see each event in Discord. Buttons and inputs
            are inert here.
          </Card.Description>
        </Card.Header>
        <Card.Content className="flex flex-col gap-4">
          {events.length === 0 ? (
            <p className="text-sm text-text-muted">
              Add an event with at least one question to preview.
            </p>
          ) : (
            <>
              <div role="tablist" aria-label="Events" className="flex flex-wrap gap-2 border-b border-border">
                {events.map((event, index) => {
                  const active = index === safeIndex;
                  const label = event.name === '' ? `Event ${index + 1}` : event.name;
                  return (
                    <button
                      key={event.clientId}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setEventIndex(index)}
                      className={
                        active
                          ? 'border-b-2 border-accent px-3 py-1.5 text-sm text-text'
                          : 'border-b-2 border-transparent px-3 py-1.5 text-sm text-text-muted hover:text-text'
                      }
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {current !== undefined ? <EventPreview event={current} /> : null}
            </>
          )}
        </Card.Content>
        <Card.Footer>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </Card.Footer>
      </Card>
    </Modal>
  );
}

function EventPreview({ event }: { event: BuilderEvent }): JSX.Element {
  const headline = event.headline === '' ? 'Untitled event' : event.headline;
  const buttonLabel = event.buttonLabel === '' ? 'Share feedback' : event.buttonLabel;

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-text">DM bubble</h3>
        <div className="rounded-lg bg-discord-surface px-5 py-4 text-discord-text">
          <div className="text-base font-semibold text-white">{headline}</div>
          <div className="mt-3">
            <span className="inline-flex cursor-not-allowed items-center gap-2 rounded-[3px] bg-discord-blurple px-3 py-1.5 text-sm font-medium text-white">
              {buttonLabel}
            </span>
          </div>
        </div>
      </section>
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-text">Modal</h3>
        <ModalPreview event={event} />
      </section>
    </div>
  );
}

function ModalPreview({ event }: { event: BuilderEvent }): JSX.Element {
  const title = event.headline === '' ? 'Untitled event' : event.headline;
  return (
    <div className="overflow-hidden rounded-lg bg-discord-surface text-discord-text shadow-xl">
      <div className="flex items-center gap-3 border-b border-discord-border px-5 py-4">
        <div
          aria-hidden
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-discord-surface-input text-[10px] font-bold text-discord-text"
        >
          PB
        </div>
        <div className="flex-1 truncate text-lg font-semibold text-white">{title}</div>
        <span aria-hidden className="text-base text-discord-text-muted">×</span>
      </div>
      <div className="max-h-[28rem] space-y-5 overflow-y-auto px-5 py-4">
        <div className="flex items-start gap-2 rounded-md border border-discord-warning bg-discord-warning-bg px-3 py-2.5 text-xs text-discord-warning">
          <span aria-hidden>⚠️</span>
          <span>
            This form will be submitted to <strong>PlayerBase</strong>. Do not
            share passwords or other sensitive information.
          </span>
        </div>
        {event.questions.length === 0 ? (
          <p className="text-xs text-discord-text-faint">No questions yet.</p>
        ) : null}
        {event.questions.map((question) => (
          <QuestionPreview key={question.clientId} question={question} />
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 bg-discord-surface px-5 py-3">
        <span className="cursor-not-allowed rounded-[3px] px-4 py-1.5 text-sm text-white hover:bg-white/5">
          Cancel
        </span>
        <span className="cursor-not-allowed rounded-[3px] bg-discord-blurple px-4 py-1.5 text-sm font-medium text-white">
          Submit
        </span>
      </div>
    </div>
  );
}

function QuestionPreview({ question }: { question: BuilderQuestion }): JSX.Element {
  const title =
    question.title === '' ? (
      <em className="text-discord-text-faint">Untitled question</em>
    ) : (
      question.title
    );
  const requiredMark = question.required ? (
    <span className="text-discord-danger"> *</span>
  ) : null;
  return (
    <div className="space-y-1.5">
      <div className="text-sm font-semibold text-white">
        {title}
        {requiredMark}
      </div>
      {question.description !== null && question.description !== '' ? (
        <div className="text-xs text-discord-text-muted">{question.description}</div>
      ) : null}
      <div className="pt-1">
        <QuestionControlPreview question={question} />
      </div>
    </div>
  );
}

function QuestionControlPreview({ question }: { question: BuilderQuestion }): JSX.Element {
  switch (question.kind) {
    case 'short_text':
      return <DiscordTextInput rows={1} />;
    case 'long_text':
      return <DiscordTextInput rows={3} />;
    case 'rating': {
      const display = resolveRatingDisplay(question.config);
      const emoji = display?.mode === 'emoji' ? display.emoji : null;
      return (
        <DiscordRadioStack
          items={RATING_VALUES.map((n) => ({
            key: String(n),
            label: emoji !== null ? emoji.repeat(n) : String(n),
          }))}
        />
      );
    }
    case 'select': {
      const { maxValues } = resolveSelectConfig(question.config);
      const multi = maxValues > 1;
      return (
        <div className="space-y-2">
          {multi ? (
            <div className="text-[11px] text-discord-text-faint">Pick up to {maxValues}</div>
          ) : null}
          <DiscordSelect />
        </div>
      );
    }
    case 'severity':
      return (
        <DiscordRadioStack items={SEVERITY_LEVELS.map((label) => ({ key: label, label }))} />
      );
    case 'binary': {
      const { trueLabel, falseLabel } = resolveBinaryLabels(question.config);
      return (
        <DiscordRadioStack
          items={[
            { key: 'true', label: trueLabel },
            { key: 'false', label: falseLabel },
          ]}
        />
      );
    }
    case 'nps':
      return (
        <DiscordRadioStack
          items={NPS_OPTIONS.map((option) => ({
            key: String(option.value),
            label: option.label,
          }))}
        />
      );
    default: {
      const _exhaustive: never = question.kind;
      return <>{_exhaustive}</>;
    }
  }
}

function DiscordTextInput({ rows }: { rows: number }): JSX.Element {
  const minHeight = rows === 1 ? '2.25rem' : `${rows * 1.4 + 0.75}rem`;
  return (
    <div
      aria-hidden
      className="w-full rounded-[3px] bg-discord-surface-input px-2 py-1.5"
      style={{ minHeight }}
    />
  );
}

function DiscordSelect(): JSX.Element {
  return (
    <div
      aria-hidden
      className="flex w-full items-center justify-between rounded-[3px] border border-transparent bg-discord-surface-input px-3 py-2 text-sm text-discord-text-faint"
    >
      <span>Make a selection</span>
      <span aria-hidden className="text-xs">▾</span>
    </div>
  );
}

function DiscordRadioStack({
  items,
}: {
  items: Array<{ key: string; label: string }>;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      {items.map((item) => (
        <div key={item.key} className="flex items-center gap-3">
          <span
            aria-hidden
            className="h-4 w-4 shrink-0 rounded-full border border-discord-text-faint"
          />
          <span className="text-sm text-discord-text">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

